import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  inject,
  model,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import {AlertComponent, AlertOptions} from "@shared/components/alert/alert.component";
import {AlertTypes} from '@core/enums/alert-types';
import {TranslateService, Translation} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {PatientService} from '@core/services/patient.service';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {HTTPMethods, ModalTypes} from '@core/enums';
import {finalize} from 'rxjs';
import {APIService} from '@core/services';
import {ButtonComponent, ButtonOptions, DateComponent, ModalComponent, ModalOptions} from '@shared/components';
import {DropdownComponent} from '@shared/components/dropdown/dropdown.component';
import {FormsModule} from '@angular/forms';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';

@UntilDestroy()
@Component({
  selector: 'ncp-view',
  imports: [
    AlertComponent,
    FormsModule,
    ModalComponent,
    ButtonComponent
  ],
  templateUrl: './view.component.html',
  styleUrl: './view.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ViewComponent implements OnInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('documentPreview')
  private documentPreviewEl?: ElementRef<HTMLElement>;

  @ViewChild('printBtn') public printBtn!: ButtonComponent;
  @ViewChild('cdaBtn') public cdaBtn!: ButtonComponent;

  private _http: HttpClient = inject(HttpClient);
  private apiService = inject(APIService);
  private readonly translate = inject(TranslateService);
  private readonly patientService = inject(PatientService);
  protected translations!: Translation;
  protected alertText = '';

  protected previewUrl = signal<SafeResourceUrl | null>(null);
  protected previewType = signal<string>('application/pdf');

  private sanitizer = inject(DomSanitizer);
  private objectUrl: string | null = null;

  protected viewAlertOptions = signal<AlertOptions>({
    id: 'viewAlert',
    type: AlertTypes.info,
    title: 'patient_record_version',
    show: this.patientService.version?.latest_dt != null && this.patientService.version?.latest_dt != "" && this.patientService.version?.latest_dt != undefined,
  });

  protected printBtnOptions = signal<ButtonOptions>(
    new ButtonOptions({
      class: 'btn badge btn-primary p-2 ms-1 my-0 border-0',
      isLoading: false,
      icon: 'bi bi-file-earmark-pdf fs-5',
      tooltip: 'preview_pdf',
      onClick: () => {
        this.print();
      },
    })
  );

  protected cdaBtnOptions = signal<ButtonOptions>(
    new ButtonOptions({
      class: 'btn badge btn-primary p-2 ms-1 my-0 border-0',
      isLoading: false,
      icon: 'bi bi-file-earmark-spreadsheet fs-5',
      tooltip: 'preview_cda',
      onClick: () => {
        this.cda();
      },
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      title: "preview_document",
      type: ModalTypes.empty,
      options: {
        animation: true,
        fullscreen: true,
        backdrop: 'static',
        keyboard: false,
      },
    })
  );

  ngOnInit() {
    this.translations = this.translate.instant('alert');
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('alert');

      if(this.patientService.version?.latest_dt != null && this.patientService.version?.latest_dt != "" && this.patientService.version?.latest_dt != undefined) {
        this.alertText = this.translations['patient_version_info'].replace('{1}', this.patientService.version.latest_dt).replace('{2}', this.patientService.version.latest_author);
      }
    });

    if(this.patientService.version?.latest_dt != null && this.patientService.version?.latest_dt != "" && this.patientService.version?.latest_dt != undefined) {
      this.alertText = this.translations['patient_version_info'].replace('{1}', this.patientService.version.latest_dt).replace('{2}', this.patientService.version.latest_author);
    }
  }

  private get headers(): HttpHeaders {
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('jwt', this.patientService.patient?.token || '')
      .set('pToken', this.patientService.pToken || '');
  }

  print() {
    this.printBtnOptions.update((options) => ({
      ...options,
      isLoading: true,
    }));

    this.apiService
      .call({
        path: '/get-ps_pdf',
        headers: this.headers,
        type: HTTPMethods.POST,
        body: {
          dob: this.patientService.patient?.data?.dob,
        }
      })
      ?.pipe(
        finalize(() => {
          this.printBtnOptions.update((options) => ({
            ...options,
            isLoading: false,
          }));
        })
      )
      .subscribe({
        next: (res: any) => {
          const base64 = res?.srv_results?.data;

          if (res?.status === 1 && res?.srv_results?.status === 1 && base64) {
            this.previewPdf(base64);
            this.printBtn.blur();
            this.mod.open();
            return;
          }

          console.error('Unable to load ps PDF:', res);
        },
        error: (error) => {
          console.error('Error loading ps PDF:', error);
        },
      });
  }

  cda(): void {
    this.cdaBtnOptions.update((options) => ({
      ...options,
      isLoading: true,
    }));

    this.apiService
      .call({
        path: '/get-cda',
        headers: this.headers,
        type: HTTPMethods.POST,
        body: {
          dob: this.patientService.patient?.data?.dob,
          document_id: 'PSid.undefined.cda'
        },
      })
      ?.pipe(
        finalize(() => {
          this.cdaBtnOptions.update((options) => ({
            ...options,
            isLoading: false,
          }));
        })
      )
      .subscribe({
        next: (res: any) => {
          const base64 = res?.srv_results?.data;

          if (res?.status === 1 && res?.srv_results?.status === 1 && base64) {
            this.previewCda(base64);
            this.cdaBtn.blur();
            this.mod.open();
            return;
          }

          console.error('Unable to load CDA:', res);
        },
        error: (error) => {
          console.error('Error loading CDA:', error);
        },
      });
  }

  protected previewPdf = (base64: string): void => {
    if (!base64) {
      return;
    }

    try {
      const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

      const byteChars = atob(cleanBase64);
      const byteNumbers = new Array(byteChars.length);

      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: 'application/pdf',
      });

      this.objectUrl = URL.createObjectURL(blob);

      this.previewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl));

      this.previewType.set('application/pdf');
    } catch (error) {
      console.error('Unable to preview PDF:', error);
    }
  };

  protected previewCda = (base64: string): void => {
    if (!base64) {
      return;
    }

    try {
      const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

      const dataUrl = `data:text/html;base64,${cleanBase64}`;

      this.previewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl));

      this.previewType.set('text/html');

    } catch (error) {
      console.error('Unable to preview CDA:', error);
    }
  };
}
